import React, { useRef, useState, useCallback } from 'react';
import { useParams, useLocation, useHistory, Link } from 'react-router-dom';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import styled from 'styled-components';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { API_URL, ROLES } from '../../../../utils/constants';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { DateRangeFilterCell } from '~/src/components/common/grid/DateRangeFilterCell';
import { DateCell } from '~/src/components/common/grid/Cells';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import GridWrapper from '~/src/components/common/GridWrapper';
import GtinComponent from '~/src/components/common/GtinComponent';
import RolesDependent from '~/src/components/common/RolesDependent';
import ProductInStockModal from '../components/ProductInStockModal';
import TableButton from '~/src/components/common/forms/Button/TableButton';

export default function InventoryForWarehouse(): JSX.Element {
  const stateRef = useRef(null);
  const history = useHistory();
  const { warehouseId } = useParams<{ warehouseId: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const warehouseName = queryParams.get('warehouseName') || 'Unknown Warehouse';

  const url = `${API_URL}/inventory/get/${warehouseId}`;
  const StatefullGrid = useCallback(withState(Grid, url), [url]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const barcodeCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <GtinComponent text="CU" code={dataItem?.cuGtin} />
        <GtinComponent text="DU" code={dataItem?.duGtin} />
      </td>
    );
  }, []);

  const productNameCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <Link to={`/inventory/list-for-generic/in-warehouse/${warehouseId}/${dataItem.product.genericProductId}?warehouseName=${warehouseName}`}>
          {dataItem.product.genericProduct.name}
        </Link>
      </td>
    );
  }, []);

  const unitsCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        {`${dataItem.fullDuUnits}/${dataItem.singleDpUnits}/${dataItem.singleCuUnits}`}
      </td>
    );
  }, []);

  const handleOpenModal = useCallback((productId: string) => {
    setSelectedProductId(productId);
    setIsModalOpen(true);
  }, []);

  const periodInStockCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <TableButton
          title="View Period In Stock Info"
          onClick={() => handleOpenModal(dataItem.product.genericProductId)}
        />
      </td>
    );
  }, [handleOpenModal]);

  return (
    <WhitePad isFluid>
      <TitleComponent title={`Inventory for ${warehouseName}`} />
      <LinkWrapper>
        <SecondaryButton
          isBig
          onClick={(): void => history.push('/inventory/select-product')}
          title="New Incoming Products"
        />
        <SecondaryButton isBig onClick={(): void => history.push(`/inventory/reports/?warehouseId=${warehouseId}`)} title="Reports" />

      </LinkWrapper>
      <SubTitle>Current Stock</SubTitle>
      <GridWrapper>
        <StatefullGrid ref={stateRef}>
          <GridColumn
            field="name"
            title="Product Name"
            cell={productNameCell} />
          <GridColumn
            field="gtin"
            title="Barcode"
            cell={barcodeCell} />
          <GridColumn
            field="totalCuUnits"
            title="Total Units" />
          <GridColumn
            field="fullDuUnits"
            title="Full Boxes"
            cell={unitsCell} />
          <GridColumn
            field="expirationDate"
            title="Expiration Date"
            filterCell={DateRangeFilterCell}
            cell={DateCell}
          />
          <GridColumn
            field="periodInStock"
            title="Period In Stock"
            sortable={false}
            filterable={false}
            cell={periodInStockCell}
          />
        </StatefullGrid>
      </GridWrapper>
      {selectedProductId && (
        <ProductInStockModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          productId={selectedProductId}
          warehouseId={warehouseId}
        />
      )}
    </WhitePad>
  );
}

const SubTitle = styled.h3`
  color: #a7a7a7;
  font-size: 25px;
  font-weight: 700;
  margin: 0;
  line-height: 29px;
`;

const LinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;